import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Upload, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const BeforeAfterPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const [form, setForm] = useState({
    title: '',
    description: '',
    service_name: '',
    before_image: '',
    after_image: ''
  });

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff' || isAdmin;

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/before-after`);
      setGallery(res.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Imagem carregada!');
    } catch (error) {
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.before_image || !form.after_image) {
      toast.error('Por favor, carregue as imagens Antes e Depois');
      return;
    }

    try {
      if (selectedItem) {
        await axios.put(`${API}/before-after/${selectedItem.item_id}`, form, { withCredentials: true });
        toast.success('Atualizado!');
      } else {
        await axios.post(`${API}/before-after`, form, { withCredentials: true });
        toast.success('Adicionado à galeria!');
      }
      setDialogOpen(false);
      setForm({ title: '', description: '', service_name: '', before_image: '', after_image: '' });
      setSelectedItem(null);
      fetchGallery();
    } catch (error) {
      toast.error('Erro ao guardar');
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Tem certeza que deseja apagar?')) return;
    try {
      await axios.delete(`${API}/before-after/${itemId}`, { withCredentials: true });
      toast.success('Apagado!');
      fetchGallery();
    } catch (error) {
      toast.error('Erro ao apagar');
    }
  };

  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const navigateViewer = (direction) => {
    setViewerIndex(prev => {
      const newIndex = prev + direction;
      if (newIndex < 0) return gallery.length - 1;
      if (newIndex >= gallery.length) return 0;
      return newIndex;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#BFA05F] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-sm uppercase tracking-widest text-[#BFA05F] font-medium">
            {language === 'pt' ? 'Resultados Reais' : 'Real Results'}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#1A1815] mt-4">
            {language === 'pt' ? 'Antes & Depois' : 'Before & After'}
          </h1>
          <p className="text-lg text-[#635E54] mt-4 max-w-2xl mx-auto">
            {language === 'pt' 
              ? 'Veja as transformações incríveis das nossas clientes' 
              : 'See the incredible transformations of our clients'}
          </p>
        </div>

        {/* Add Button (Staff/Admin only) */}
        {isStaff && (
          <div className="flex justify-center mb-8">
            <Button
              onClick={() => { setSelectedItem(null); setForm({ title: '', description: '', service_name: '', before_image: '', after_image: '' }); setDialogOpen(true); }}
              className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full px-6"
            >
              <Plus className="w-4 h-4 mr-2" />
              {language === 'pt' ? 'Adicionar Transformação' : 'Add Transformation'}
            </Button>
          </div>
        )}

        {/* Gallery Grid */}
        {gallery.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {gallery.map((item, index) => (
              <div 
                key={item.item_id}
                className="bg-white rounded-2xl overflow-hidden border border-[#E6E2DA] hover:shadow-xl transition-shadow cursor-pointer group"
                onClick={() => openViewer(index)}
              >
                {/* Before/After Slider */}
                <div className="relative h-64 overflow-hidden">
                  <div className="absolute inset-0 flex">
                    <div className="w-1/2 h-full overflow-hidden">
                      <img 
                        src={getFullImageUrl(item.before_image)} 
                        alt="Antes"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {language === 'pt' ? 'ANTES' : 'BEFORE'}
                      </span>
                    </div>
                    <div className="w-1/2 h-full overflow-hidden">
                      <img 
                        src={getFullImageUrl(item.after_image)} 
                        alt="Depois"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute top-2 right-2 bg-[#BFA05F] text-white text-xs px-2 py-1 rounded">
                        {language === 'pt' ? 'DEPOIS' : 'AFTER'}
                      </span>
                    </div>
                  </div>
                  <div className="absolute inset-y-0 left-1/2 w-0.5 bg-white" />
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-heading text-lg font-medium text-[#1A1815]">
                    {item.title || item.service_name}
                  </h3>
                  {item.description && (
                    <p className="text-sm text-[#635E54] mt-1 line-clamp-2">{item.description}</p>
                  )}
                  {item.service_name && (
                    <span className="inline-block mt-2 text-xs bg-[#F2F0EB] text-[#635E54] px-2 py-1 rounded-full">
                      {item.service_name}
                    </span>
                  )}
                </div>

                {/* Admin Actions */}
                {isStaff && (
                  <div className="px-4 pb-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setForm(item); setDialogOpen(true); }}
                      className="rounded-full flex-1"
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.item_id); }}
                      className="rounded-full text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-[#635E54]">
              {language === 'pt' 
                ? 'Ainda não há transformações na galeria.' 
                : 'No transformations in the gallery yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedItem 
                ? (language === 'pt' ? 'Editar Transformação' : 'Edit Transformation')
                : (language === 'pt' ? 'Nova Transformação' : 'New Transformation')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Título' : 'Title'}</label>
              <Input 
                value={form.title} 
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Serviço' : 'Service'}</label>
              <Input 
                value={form.service_name} 
                onChange={(e) => setForm({ ...form, service_name: e.target.value })}
                placeholder={language === 'pt' ? 'Ex: Limpeza de Pele' : 'Ex: Facial Cleansing'}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Descrição' : 'Description'}</label>
              <Textarea 
                value={form.description} 
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Foto Antes' : 'Before Photo'}</label>
                <div className="mt-1">
                  {form.before_image ? (
                    <div className="relative">
                      <img src={getFullImageUrl(form.before_image)} alt="" className="w-full h-32 object-cover rounded-lg" />
                      <button 
                        onClick={() => setForm({ ...form, before_image: '' })}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E6E2DA] rounded-lg cursor-pointer hover:border-[#BFA05F]">
                      <Upload className="w-6 h-6 text-[#A8A29A]" />
                      <span className="text-xs text-[#635E54] mt-1">Carregar</span>
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'before_image')} />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Foto Depois' : 'After Photo'}</label>
                <div className="mt-1">
                  {form.after_image ? (
                    <div className="relative">
                      <img src={getFullImageUrl(form.after_image)} alt="" className="w-full h-32 object-cover rounded-lg" />
                      <button 
                        onClick={() => setForm({ ...form, after_image: '' })}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E6E2DA] rounded-lg cursor-pointer hover:border-[#BFA05F]">
                      <Upload className="w-6 h-6 text-[#A8A29A]" />
                      <span className="text-xs text-[#635E54] mt-1">Carregar</span>
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'after_image')} />
                    </label>
                  )}
                </div>
              </div>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={uploading}
              className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full"
            >
              {language === 'pt' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Viewer */}
      {viewerOpen && gallery[viewerIndex] && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center">
          <button 
            onClick={() => setViewerOpen(false)}
            className="absolute top-4 right-4 text-white p-2"
          >
            <X className="w-8 h-8" />
          </button>
          <button 
            onClick={() => navigateViewer(-1)}
            className="absolute left-4 text-white p-2"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button 
            onClick={() => navigateViewer(1)}
            className="absolute right-4 text-white p-2"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          
          <div className="flex gap-4 max-w-5xl mx-4">
            <div className="flex-1">
              <span className="text-white text-sm mb-2 block text-center">ANTES</span>
              <img 
                src={getFullImageUrl(gallery[viewerIndex].before_image)} 
                alt="Antes"
                className="max-h-[70vh] object-contain mx-auto rounded-lg"
              />
            </div>
            <div className="flex-1">
              <span className="text-[#BFA05F] text-sm mb-2 block text-center">DEPOIS</span>
              <img 
                src={getFullImageUrl(gallery[viewerIndex].after_image)} 
                alt="Depois"
                className="max-h-[70vh] object-contain mx-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeforeAfterPage;
